<template>
  <div class="card-maquina" :style="cardBorderStyle">
    <div class="side-status-bar" :style="{ background: colorEstatus(item.status) }"></div>

    <!-- HEADER -->
    <div class="card-header">
      <div class="machine-id-block">
        <div class="machine-label-row">
          <v-icon size="13" :color="colorEstatus(item.status)" class="mr-1 machine-cog-icon">mdi-cog</v-icon>
          <span class="machine-label">Máquina</span>
        </div>
        <span class="machine-code">{{ item.codigoMaquina }}</span>
      </div>
      <div class="header-right">
        <div class="status-pill" :style="pillStyle">
          <span class="status-led" :style="{ background: colorEstatus(item.status) }"></span>
          {{ labelEstatus(item.status) }}
        </div>
      </div>
    </div>

    <!-- CON ODT EN CURSO -->
    <template v-if="item.status === 1">

      <!-- DATOS DE LA ODT -->
      <div class="info-section">
        <div class="info-row">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-clipboard-text-outline</v-icon>
            ODT
          </span>
          <span class="info-val">{{ item.claveOdt || '—' }}</span>
        </div>

        <div v-if="item.codigoMolde" class="info-row info-row-molde">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-package-variant</v-icon>
            Molde
          </span>
          <span class="info-val molde-val">{{ item.codigoMolde }}</span>
        </div>

        <div class="info-row">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-timer-outline</v-icon>
            Operando
          </span>
          <span class="info-val">{{ tiempoOperandoTxt }}</span>
        </div>
      </div>

      <!-- BARRA DE AVANCE META -->
      <div class="meta-progress">
        <div class="meta-progress-head">
          <span class="meta-progress-label">Avance</span>
          <span class="meta-progress-pct">{{ avancePct }}%</span>
        </div>
        <div class="meta-bar-wrap">
          <div class="meta-bar-fill" :style="{ width: Math.min(100, avancePct) + '%' }"></div>
        </div>
      </div>

      <!-- TC REAL / RATE PROMEDIO -->
      <div class="metric-row">
        <div class="metric-box">
          <span class="metric-label">TC Receta</span>
          <span class="metric-value">{{ tcRealTxt }}</span>
        </div>
        <div class="metric-box">
          <span class="metric-label">Rate Programado</span>
          <span class="metric-value">{{ rateTxt }}</span>
        </div>
      </div>

      <div class="h-divider"></div>

      <!-- PRODUCCIÓN -->
      <div class="prod-section">
        <div class="prod-box">
          <span class="prod-box-label">Programadas</span>
          <span class="prod-box-value">{{ fmt(item.meta) }}</span>
        </div>
        <div class="prod-box prod-box-ok">
          <span class="prod-box-label">Piezas Ok</span>
          <span class="prod-box-value val-ok">{{ fmt(item.piezasOk) }}</span>
        </div>
        <div class="prod-box prod-box-no-ok">
          <span class="prod-box-label">Piezas No Ok</span>
          <span class="prod-box-value val-no-ok">{{ fmt(item.piezasNoOk) }}</span>
        </div>
      </div>

      <!-- ARTÍCULOS -->
      <template v-if="articulos.length">
        <div class="h-divider"></div>
        <div class="articulos-section">
          <div class="cavidades-row">Cavidades en producción: {{ cavidadesEnProduccion }}</div>
          <p class="articulos-title">Artículos en producción</p>
          <div v-for="(art, idx) in articulos" :key="'art-' + idx" class="articulo-row">
            <div class="articulo-info">
              <span class="articulo-nombre">{{ art.nombre || art.codigo || 's/n' }}</span>
              <span v-if="art.codigo && art.nombre" class="articulo-codigo">{{ art.codigo }}</span>
            </div>
            <span class="articulo-progreso">
              {{ fmt(art.reportadas) }} / {{ fmt(art.meta) }}
            </span>
          </div>
        </div>
      </template>

    </template>

    <!-- SIN ODT -->
    <template v-else>
      <div class="inactive-state">
        <div class="inactive-icon">
          <v-icon size="22" color="#8A95A3">mdi-clipboard-remove-outline</v-icon>
        </div>
        <p class="inactive-text">Sin ODT en curso</p>
      </div>
    </template>

  </div>
</template>

<script>
const STATUS_MAP = {
  1: { label: 'Con ODT', color: '#00C472' },
  2: { label: 'Sin ODT', color: '#8A95A3' },
};

export default {
  name: 'CardODTMaquina',

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    articulos() {
      return Array.isArray(this.item?.articulos) ? this.item.articulos : [];
    },

    // Cavidades en producción: cantidad de IdArticulo distintos entre los artículos en curso.
    cavidadesEnProduccion() {
      const ids = this.articulos
        .map(a => Number(a?.idArticulo))
        .filter(id => Number.isFinite(id) && id > 0);
      return new Set(ids).size;
    },

    avancePct() {
      const meta = Number(this.item?.meta) || 0;
      const ok = Number(this.item?.piezasOk) || 0;
      if (meta <= 0) return 0;
      return Math.round((ok / meta) * 100);
    },

    rateTxt() {
      const rate = Number(this.item?.promedioRate) || 0;
      return rate > 0 ? `${rate} pz/h` : '—';
    },

    tcRealTxt() {
      // TC real efectivo derivado del rate promedio (piezas/hora) → segundos por pieza.
      const rate = Number(this.item?.promedioRate) || 0;
      if (rate <= 0) return '—';
      const seg = 3600 / rate;
      return `${seg.toFixed(seg >= 10 ? 0 : 1)} seg`;
    },

    tiempoOperandoTxt() {
      const s = Number(this.item?.tiempoOperando) || 0;
      if (s <= 0) return '—';
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = n => String(n).padStart(2, '0');
      return h > 0 ? `${h}h ${pad(m)}m` : `${pad(m)}m ${pad(sec)}s`;
    },

    cardBorderStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        border: `1.5px solid ${c}30`,
        boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px ${c}15`,
      };
    },

    pillStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        color: c,
        background: c + '14',
        borderColor: c + '40',
      };
    },
  },

  methods: {
    fmt(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '0';
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    },

    colorEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[2]).color;
    },

    labelEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[2]).label;
    },
  },
};
</script>

<style scoped>
/* ===================================================
   TOKENS
   =================================================== */
.card-maquina {
  --radius-card: 20px;
  --radius-inner: 12px;
  --radius-sm: 8px;
  --clr-surface: #F0F2F6;
  --clr-border: rgba(0, 0, 0, 0.07);
  --clr-text-primary: #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted: #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  --font-body: 'DM Sans', 'Roboto', sans-serif;
}

/* ===================================================
   CARD BASE
   =================================================== */
.card-maquina {
  background: #FFFFFF;
  border-radius: var(--radius-card);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font-family: var(--font-body);
  display: flex;
  flex-direction: column;
  animation: card-in 0.4s ease both;
}

.card-maquina:hover {
  transform: translateY(-3px);
}

.side-status-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  opacity: 0.92;
}

/* ===================================================
   HEADER
   =================================================== */
.card-header {
  padding: 16px 20px 14px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--clr-border);
  flex-shrink: 0;
}

.machine-id-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.machine-label-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 2px;
}

.machine-cog-icon {
  animation: machine-cog-spin 2.8s linear infinite;
}

@keyframes machine-cog-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.machine-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
}

.machine-code {
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border: 1.5px solid;
  transition: background 0.3s, color 0.3s;
}

.status-led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ===================================================
   DIVIDER
   =================================================== */
.h-divider {
  height: 1px;
  background: var(--clr-border);
  margin: 0 20px;
  flex-shrink: 0;
}

/* ===================================================
   INFO SECTION
   =================================================== */
.info-section {
  padding: 12px 20px 4px;
}

.info-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.info-row:last-child {
  border-bottom: none;
}

.info-key {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-muted);
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.info-key svg {
  opacity: 0.7;
}

.info-val {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  text-align: right;
  max-width: 58%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-row-molde {
  background: rgba(43, 126, 255, 0.04);
  border-radius: 6px;
  padding: 5px 8px !important;
  margin: 2px 0;
}

.molde-val {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 700 !important;
  color: #2B7EFF !important;
  letter-spacing: 0.04em;
}

/* ===================================================
   BARRA DE AVANCE META
   =================================================== */
.meta-progress {
  padding: 8px 20px 14px;
}

.meta-progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.meta-progress-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
}

.meta-progress-pct {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 700;
  color: #00C472;
}

.meta-bar-wrap {
  height: 7px;
  background: var(--clr-surface);
  border-radius: 10px;
  overflow: hidden;
}

.meta-bar-fill {
  height: 100%;
  border-radius: 10px;
  background: #00C472;
  opacity: 0.85;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ===================================================
   TC REAL / RATE PROMEDIO
   =================================================== */
.metric-row {
  padding: 0 20px 14px;
  display: flex;
  gap: 8px;
}

.metric-box {
  flex: 1;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  border-radius: var(--radius-sm);
  padding: 9px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.metric-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: var(--font-primary);
}

.metric-value {
  font-family: var(--font-primary);
  font-size: 16px;
  font-weight: 700;
  color: #2B7EFF;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

/* ===================================================
   PRODUCCIÓN
   =================================================== */
.prod-section {
  padding: 12px 20px 14px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 8px;
  flex-shrink: 0;
}

.prod-box {
  background: var(--clr-surface);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.prod-box-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: var(--font-primary);
}

.prod-box-value {
  font-family: var(--font-primary);
  font-size: 20px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.prod-box-ok {
  background: rgba(0, 196, 114, 0.10);
}

.prod-box-no-ok {
  background: rgba(232, 53, 53, 0.08);
}

.prod-box-value.val-ok {
  color: #00A35E;
}

.prod-box-value.val-no-ok {
  color: #D32F2F;
}

/* ===================================================
   ARTÍCULOS
   =================================================== */
.articulos-section {
  padding: 10px 20px 16px;
}

.articulos-title {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-bottom: 6px;
}

.cavidades-row {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-bottom: 6px;
}

.articulo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.articulo-row:last-child {
  border-bottom: none;
}

.articulo-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.articulo-nombre {
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  font-family: var(--font-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.articulo-codigo {
  font-size: 10px;
  color: var(--clr-text-muted);
}

.articulo-progreso {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 700;
  color: var(--clr-text-secondary);
  white-space: nowrap;
}

/* ===================================================
   INACTIVO
   =================================================== */
.inactive-state {
  padding: 32px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inactive-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--clr-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: var(--clr-text-muted);
}

.inactive-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

/* ===================================================
   ANIMACIÓN
   =================================================== */
@keyframes card-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===================================================
   RESPONSIVE
   =================================================== */
@media (max-width: 600px) {
  .machine-code {
    font-size: 18px;
  }

  .prod-box-value {
    font-size: 18px;
  }

  .card-header {
    padding: 14px 16px 12px;
  }
}

@media (min-width: 1265px) {
  .machine-code {
    font-size: 24px;
  }

  .prod-box-value {
    font-size: 22px;
  }
}

@media screen and (min-width: 3200px) {
  .machine-code {
    font-size: 2.8rem;
  }

  .prod-box-value {
    font-size: 1.8rem;
  }

  .info-key {
    font-size: 14px;
  }

  .info-val {
    font-size: 15px;
  }

  .status-pill {
    font-size: 14px;
    padding: 8px 16px;
  }

  .prod-box-label {
    font-size: 13px;
  }

  .articulo-nombre {
    font-size: 14px;
  }

  .articulo-progreso {
    font-size: 14px;
  }
}

@media (hover: none) {
  .card-maquina:hover {
    transform: none;
  }
}
</style>
